import type { SetupState } from '@tandemone/types'
import { type LucideIcon, Palette, ShieldCheck } from 'lucide-react'

import { getSharedSteps, getSupplierSections } from '@/domains/onboarding'

import { SETTINGS_NAV_HREF } from './nav-groups'

export type SettingsNavItem = {
  label: string
  href: string
  icon: LucideIcon
}

export type SettingsNavGroup = {
  label: string
  items: SettingsNavItem[]
}

const ONBOARDING_PREFIX = '/onboarding'
const SETTINGS_PREFIX = '/settings'

/** the onboarding step helpers own the canonical titles, icons, and routes.
 * settings reuses the same data and only swaps the route prefix so a new
 * onboarding step shows up here automatically, no second list to maintain.
 * a step whose settings url deliberately differs carries its own settingsHref.
 */
function toSettingsHref(step: {
  ctaHref: string
  settingsHref?: string
}): string {
  if (step.settingsHref) return step.settingsHref
  return step.ctaHref.startsWith(ONBOARDING_PREFIX)
    ? `${SETTINGS_PREFIX}${step.ctaHref.slice(ONBOARDING_PREFIX.length)}`
    : step.ctaHref
}

export function getSettingsNav(setup: SetupState): SettingsNavGroup[] {
  const accountGroup: SettingsNavGroup = {
    label: 'Account',
    items: [
      {
        label: 'Account & security',
        href: SETTINGS_NAV_HREF,
        icon: ShieldCheck
      },
      {
        label: 'Appearance',
        href: '/settings/appearance',
        icon: Palette
      }
    ]
  }

  /** Super scope has no company, so the company and supplier groups have
   * nothing to point at. */
  if (setup.scope === 'super' || setup.isOnboarding) return [accountGroup]

  const groups: SettingsNavGroup[] = [
    accountGroup,
    {
      label: 'Company',
      items: [
        ...getSharedSteps(setup).map(step => ({
          label: step.title,
          href: toSettingsHref(step),
          icon: step.icon
        }))
      ]
    }
  ]

  if (setup.isSupplier) {
    for (const section of getSupplierSections(setup)) {
      /** documents live in the top-level Documents tab. quality assessment
       * (incl. measurement) is an onboarding-only step set, not edited after.
       * plan lives under the top-level Billing tab. none belong in settings.
       */
      if (
        section.info.id === 'documents' ||
        section.info.id === 'quality_assessment' ||
        section.info.id === 'plan'
      ) {
        continue
      }
      groups.push({
        label: section.info.title,
        items: section.items.map(item => ({
          label: item.title,
          href: toSettingsHref(item),
          icon: item.icon
        }))
      })
    }
  }

  return groups
}
